import { Button } from '@mui/material'
import { memo, useCallback, useState } from 'react'
import Icon from '../../../../../../components/icon'
import TagCaptureModal from './TagCaptureModal'
import { toast } from 'react-toastify'

/**
 * Captures the current frame from a video element as a Blob
 * @param {HTMLVideoElement} videoElement - The video element to capture from
 * @returns {Promise<Blob|null>} Promise that resolves with Blob or null if capture fails
 */
const captureVideoFrame = (videoElement) => {
  return new Promise((resolve) => {
    if (!videoElement) {
      resolve(null)
      return
    }

    // Check if video has metadata
    if (videoElement.readyState < 1) {
      console.warn('Video not ready. ReadyState:', videoElement.readyState)
      resolve(null)
      return
    }

    // Check if video has valid dimensions
    if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
      console.warn('Video has no dimensions')
      resolve(null)
      return
    }

    try {
      const canvas = document.createElement('canvas')
      canvas.width = videoElement.videoWidth
      canvas.height = videoElement.videoHeight

      const ctx = canvas.getContext('2d')
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

      // Convert canvas to blob (more efficient than base64)
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            console.error('Failed to create blob from canvas')
            resolve(null)
          }
        },
        'image/jpeg',
        0.95
      )
    } catch (error) {
      console.error('Error capturing video frame:', error)

      if (error.name === 'SecurityError') {
        console.error(
          'CORS error: Ensure crossOrigin="anonymous" is set on video element'
        )
      }

      resolve(null)
    }
  })
}

const AddTag = ({
  paused,
  attemptId,
  webcamRef,
  screenShareRef,

  timelinePosition,
  startTime,
  handleRefresh
}) => {
  const [isCapturing, setIsCapturing] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [capturedData, setCapturedData] = useState(null)

  const handleAddTag = useCallback(async () => {
    if (!paused || isCapturing) return

    if (!startTime || timelinePosition == null) {
      toast.error('Select the time from the timeline to add the tag')
      return
    }

    setIsCapturing(true)

    const timestamp = startTime ? new Date(startTime) : null
    timestamp.setSeconds(timestamp.getSeconds() + timelinePosition)

    const tagData = {
      timestamp: timestamp.toISOString(),
      webcam: null,
      screen: null
    }

    // Capture webcam data
    if (webcamRef?.current?.ref) {
      const videoElement = webcamRef.current.ref
      const frameBlob = await captureVideoFrame(videoElement)

      tagData.webcam = {
        frame: frameBlob, // Now stores Blob instead of base64
        filename: webcamRef.current.fileName,
        duration: webcamRef.current.duration || 0,
        currentTime: webcamRef.current.currentTime || 0
      }
    }

    // Capture screen share data
    if (screenShareRef?.current?.ref) {
      const videoElement = screenShareRef.current.ref
      const frameBlob = await captureVideoFrame(videoElement)

      tagData.screen = {
        frame: frameBlob, // Now stores Blob instead of base64
        filename: screenShareRef.current.fileName,
        duration: screenShareRef.current.duration || 0,
        currentTime: screenShareRef.current.currentTime || 0
      }
    }

    // Open modal with captured data
    setCapturedData(tagData)
    setModalOpen(true)
    setIsCapturing(false)
  }, [
    paused,
    webcamRef,
    screenShareRef,
    isCapturing,
    startTime,
    timelinePosition
  ])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
    setCapturedData(null)
  }, [])

  const isDisabled = !paused || isCapturing

  return (
    <>
      <Button
        onClick={handleAddTag}
        disabled={isDisabled}
        variant="contained"
        size="small"
        startIcon={
          <Icon
            icon="material-symbols:model-training"
            style={{ fontSize: '14px' }}
          />
        }
        sx={{ padding: '5px 10px', fontSize: '12px' }}
      >
        {isCapturing ? 'Capturing...' : 'Add Tag'}
      </Button>

      <TagCaptureModal
        open={modalOpen}
        attemptId={attemptId}
        onClose={handleCloseModal}
        tagData={capturedData}
        handleRefresh={handleRefresh}
      />
    </>
  )
}

export default memo(AddTag)
